import { useEffect, useRef, useState } from 'react'
import { useGetLicenseFeaturesQuery } from 'react-common'
import { useEffectOnce } from 'react-use'

import { FederationRoot, useFederationRoot } from '@constructor/federation/base'
import { createFederationRootDataFabric, createFederationRouterFabric } from '@constructor/federation/react'

import { createRequester } from '@constructor/services'
import { useAuth } from '@/features/auth/useAuth'
import { useAiChat } from '@/features/km/aiChat'
import { isNewLearnerMF } from '@/features/learnerMF/isNewLearnerMF'
import { appRouter } from '@/index'
import i18n from '@/plugins/i18n'

export const useInitMicroFrontends = () => {
  const [federationRoot, setFederationRoot] = useState<FederationRoot>()
  const [isLoaded, setIsLoaded] = useState(false)
  const [failedFragments, setFailedFragments] = useState<string[]>([])
  const { user } = useAuth()

  const rootDataFabricRef = useRef(createFederationRootDataFabric())

  const { data: licensedFeaturesResponse } = useGetLicenseFeaturesQuery()

  useEffectOnce(() => {
    const requester = createRequester()

    const federationRoot = useFederationRoot({
      processException: (error: any) => {
        console.error(error)
      },
      requester,
      i18next: i18n,
    })

    federationRoot.setRouterFabric(createFederationRouterFabric({ router: appRouter }))

    const fragmentNames = [
      'calendar-widget',
      'calendar-main',
      'knowledge-center',
      'communications-hub',
      'promo-banners-widget',
      'learner-dashboard',
      'tasks',
      'recent-courses',
    ]

    if (isNewLearnerMF()) {
      fragmentNames.push('learner-app')
    }

    federationRoot.loadVersions({ projectName: 'learn' }).then(async () => {
      const failed: string[] = []

      const preloadPromises = fragmentNames.map(async (fragmentName) => {
        if (federationRoot.hasVersion(fragmentName)) {
          try {
            // TODO remove this condition, and use Cloud's approach
            if (fragmentName === 'knowledge-center') {
              await federationRoot.preload({ fragmentName, entryName: 'knowledgeCenterMain' })
            } else {
              await federationRoot.preload({ fragmentName })
            }
          } catch (error) {
            console.error(`Error loading fragment ${fragmentName}:`, error)
            failed.push(fragmentName)
            return Promise.reject(fragmentName)
          }
        } else {
          console.error(`Version for fragment: ${fragmentName} not found`)
          failed.push(fragmentName)
          return Promise.reject(fragmentName)
        }
      })

      try {
        await Promise.all(preloadPromises)
      } catch {
        console.error('Failed fragments:', failed)
        setFailedFragments(failed)
      } finally {
        setIsLoaded(true)
      }
    })

    const { getAiChatApi } = useAiChat(federationRoot)
    federationRoot.addRootHandler('getAiChatApi', getAiChatApi)
    federationRoot.setRootDataFabric(rootDataFabricRef.current)
    rootDataFabricRef.current.setDataGetter('user', null)
    rootDataFabricRef.current.setDataGetter('licensedFeatures', licensedFeaturesResponse)

    setFederationRoot(federationRoot)
  })

  useEffect(() => {
    if (user) {
      rootDataFabricRef.current.setDataGetter('user', user)
    }
  }, [rootDataFabricRef, user])

  useEffect(() => {
    rootDataFabricRef.current.setDataGetter('licensedFeatures', licensedFeaturesResponse)
  }, [rootDataFabricRef, licensedFeaturesResponse])

  return { federationRoot, isLoaded, failedFragments }
}
